import React, { useState, useEffect } from 'react'
import moment from 'moment'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import { Alert } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import TokTokDocSelect from '../Common/Select'
import TokTokDocDialog from '../Common/Dialog'
import LoadingButton from '../Common/LoadingButton'
import styles from './configModalStyles'
import INSIConnector from '../../utils/insi'
import { displaySnackbar } from '../../actions/main'
import { INSiConfigModalProps } from './_props'
import { useAppDispatch } from '../../store'
import useTraceInsiCall, { getInsiErrorMessage } from './useTraceInsiCall'
import { usePatientFormContext } from '../Patient/context/PatientFormContext'
import useCurrentUser from '../../hooks/useCurrentUser'

const useStyles = makeStyles(styles)
const wsIns1EmptyData = {
    cards: [],
    vitaleCard: '',
    patients: [],
    patient: '',
    cpsCard: '',
    pin: '',
}

/**
 * @description config modal
 * @param {Function} onClose set dialog
 * @param {Function} onStoredDataChange set stored data
 * @param {Function} setPatientInfoInsi set patient data for ws_ins2
 * @param {Boolean} withPatients show the list of patients
 * @param {Function} onSuccess callback when INS retrieval is successful
 */
const INSiConfigModal = ({
    onClose,
    onStoredDataChange,
    setPatientInfoInsi,
    withPatients,
    onSuccess,
}: INSiConfigModalProps) => {
    // styles
    const classes = useStyles()

    // states
    const [wsIns1Data, setWsIns1Data] = useState(wsIns1EmptyData)
    const [loadingIns, setLoadingIns] = useState(false)
    const [insiError, setInsiError] = useState<string | null>(null)
    const user = useCurrentUser()
    const dispatch = useAppDispatch()
    const { state } = usePatientFormContext()

    const { traceInsiCall } = useTraceInsiCall({
        patientId: state.patientId,
    })

    // handlers
    /**
     * @description handle close event
     * @returns {void}
     */
    const handleClose = () => {
        setWsIns1Data({
            ...wsIns1EmptyData,
        })
        onClose()
        setInsiError(null)
    }

    /**
     * @async
     * @description trying to retrieve ins with vitale cards
     * @returns {void}
     */
    const retrieveInsWithVitaleCard = async () => {
        try {
            setLoadingIns(true)

            if (withPatients) {
                await INSIConnector.openSession()
                // @ts-expect-error TS(2339) FIXME: Property 'index' does not exist on type 'string'.
                await INSIConnector.readCPxCard(wsIns1Data.cpsCard.index, wsIns1Data.cpsCard.name, wsIns1Data.pin)
                // @ts-expect-error TS(2554) FIXME: Expected 3 arguments, but got 2.
                await INSIConnector.readVitalCard(wsIns1Data.vitaleCard.index, wsIns1Data.vitaleCard.name)
                // @ts-expect-error TS(2339) FIXME: Property 'index' does not exist on type 'string'.
                const result = await INSIConnector.searchPatientWithVitaleCardIndex(wsIns1Data.patient.index, true)

                if (state.mode === 'EDITION') {
                    if (typeof result === 'number') {
                        await traceInsiCall.getPatientInsIdentityFromVitaleCard({
                            data: {
                                s_status: 'ERROR',
                                i_apiErrorCode: result,
                            },
                        })
                    } else {
                        await traceInsiCall.getPatientInsIdentityFromVitaleCard({
                            data: result.raw,
                        })
                    }
                }

                if (result) {
                    if (typeof result === 'number') {
                        if (result === 3) {
                            // multiple identities found
                            throw new Error(getInsiErrorMessage(`plusieurs identités trouvées`))
                        }

                        if (result === 1) {
                            // no identity found
                            throw new Error(getInsiErrorMessage(`aucun patient trouvé`))
                        }
                        throw new Error(getInsiErrorMessage(`inconnue`))
                    }
                    if (setPatientInfoInsi) {
                        setPatientInfoInsi(result)
                    } else {
                        throw new Error(getInsiErrorMessage(`inconnue`))
                    }
                } else {
                    throw new Error(getInsiErrorMessage(`inconnue`))
                }
            }
            const expire = moment().add(30, 'days').toDate().getTime()
            const data = {
                cpsCard: wsIns1Data.cpsCard,
                vitaleCard: wsIns1Data.vitaleCard,
                pin: wsIns1Data.pin,
                expire,
            }
            INSIConnector.setStoredData(user, data)
            onStoredDataChange(data)
            setWsIns1Data(wsIns1EmptyData)
            setLoadingIns(false)
            onClose()
            onSuccess?.()
        } catch (error) {
            console.log({
                error,
            })

            setInsiError((error as Error).message ?? 'Erreur inconnue')

            setLoadingIns(false)
        }
    }

    /**
     * @description handle vitale card change
     * @param {*} value
     * @returns {void}
     */
    const handleVitaleCardChange = (value: any) => {
        setWsIns1Data((prevData) => ({
            ...prevData,
            vitaleCard: value,
        }))
    }

    /**
     * @description handle patient change
     * @param {*} e
     * @returns {void}
     */
    const handlePatientChange = (e: any) => {
        const { value } = e.target
        setWsIns1Data((prevData) => ({
            ...prevData,
            patient: value,
        }))
    }

    /**
     * @description handle cps card change
     * @param {*} value
     * @returns {void}
     */
    const handleCpsCardChange = (value: any) => {
        setWsIns1Data((prevData) => ({
            ...prevData,
            cpsCard: value,
        }))
    }

    /**
     * @description handle pin card change
     * @param {*} value
     * @returns {void}
     */
    const handlePinChange = (value: any) => {
        setWsIns1Data((prevData) => ({
            ...prevData,
            pin: value,
        }))
    }

    useEffect(() => {
        const storedData = INSIConnector.getStoredData(user)
        if (storedData !== null && typeof storedData !== 'undefined') {
            if (storedData.cpsCard) {
                handleCpsCardChange(storedData.cpsCard)
            }
            if (storedData.vitaleCard) {
                handleVitaleCardChange(storedData.vitaleCard)
            }
            if (storedData.pin) {
                handlePinChange(storedData.pin)
            }
        }
    }, [user])

    /**
     * @async
     * @description detect the card getCardReaders
     * @returns {void}
     */
    const detectCardReaders = async () => {
        try {
            setInsiError(null)
            await INSIConnector.closeSession()
            await INSIConnector.openSession()
            const results = await INSIConnector.getCardReaders()
            setWsIns1Data({
                ...wsIns1EmptyData,
                cards: results,
                patients: [],
                patient: '',
            })
            // @ts-expect-error TS(7006) FIXME: Parameter 'reader' implicitly has an 'any' type.
            const vitaleReader = results.find((reader) => reader.value.cardInside === 2)
            // @ts-expect-error TS(7006) FIXME: Parameter 'reader' implicitly has an 'any' type.
            const cpsReader = results.find((reader) => reader.value.cardInside === 3)
            if (vitaleReader) handleVitaleCardChange(vitaleReader.value)
            if (cpsReader) handleCpsCardChange(cpsReader.value)
            if (results && results.length === 0)
                setInsiError(
                    'Aucun lecteur de carte n’a été détecté sur votre poste. Veuillez vérifier vos branchements et recommencer.',
                )
        } catch (error) {
            // @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
            setInsiError(error.message)
        }
    }

    /**
     * @description callback, when the vitale card is removed
     * @returns {void}
     */
    const vitaleCardRemoved = () => {
        dispatch(displaySnackbar('error', 'La carte Vitale a été retirée'))
        setWsIns1Data((prevData) => ({
            ...prevData,
            patients: [],
            patient: '',
        }))
    }

    /**
     * @async
     * @description read vitale cards from
     * @returns {void}
     */
    const readVitaleCard = async () => {
        try {
            await INSIConnector.openSession()
            // @ts-expect-error TS(2339) FIXME: Property 'index' does not exist on type 'string'.
            const response = await INSIConnector.isCpxCardAvailable(wsIns1Data.cpsCard.index, wsIns1Data.cpsCard.name)

            switch (response) {
                case 1: {
                    if (insiError) setInsiError(null)

                    const results = await INSIConnector.readVitalCard(
                        // @ts-expect-error TS(2339) FIXME: Property 'index' does not exist on type 'string'.
                        wsIns1Data.vitaleCard.index,
                        // @ts-expect-error TS(2339) FIXME: Property 'name' does not exist on type 'string'.
                        wsIns1Data.vitaleCard.name,
                        vitaleCardRemoved,
                    )
                    setWsIns1Data((prevData) => ({
                        ...prevData,
                        patients: results,
                    }))
                    break
                }
                case 5: {
                    setInsiError('Votre carte CPX est bloquée')
                    break
                }
                case 6: {
                    setInsiError('Votre carte CPX est périmée')
                    break
                }
                default:
                    setInsiError('Une erreur est survenue lors de la lecture de votre carte')
            }
        } catch (error) {
            // @ts-expect-error TS(2571) FIXME: Object is of type 'unknown'.
            if (error.message === 'Error: Un problème est survenue avec la carte CPS') {
                const data = {
                    cpsCard: '',
                    vitaleCard: wsIns1Data.vitaleCard,
                    pin: '',
                    // @ts-expect-error TS(2339) FIXME: Property 'expire' does not exist on type '{ cards:... Remove this comment to see the full error message
                    expire: wsIns1Data.expire,
                }
                INSIConnector.setStoredData(user, data)
                onStoredDataChange(data)
                onClose()
                dispatch(
                    displaySnackbar(
                        'error',
                        'Carte CPS absente, veuillez réintroduire votre carte CPS et saisir le code porteur !',
                    ),
                )
                // @ts-expect-error TS(2345) FIXME: Argument of type 'string' is not assignable to par... Remove this comment to see the full error message
            } else setInsiError(`${error.message}`)
        }
    }

    /**
     * @description check all data for Ws-Ins1 are present
     * @returns {Boolean}
     */
    const isAllWsIns1RequiredDataPresent = () => {
        if (withPatients)
            return (
                wsIns1Data.patient !== '' && wsIns1Data.cpsCard !== '' && wsIns1Data.pin && wsIns1Data.pin.length === 4
            )
        return wsIns1Data.cpsCard !== '' && wsIns1Data.pin && wsIns1Data.pin.length === 4
    }

    const title = withPatients
        ? "Récupérer l'identité INS à partir de la carte vitale"
        : 'Configurer les lecteurs de cartes'

    INSIConnector.setMonitoringCpxCallback(() => {
        dispatch(displaySnackbar('error', 'La carte CPS a été retirée'))
        setWsIns1Data((prevData) => ({
            ...prevData,
            patients: [],
            patient: '',
        }))
    })

    return (
        <TokTokDocDialog
            isOpen
            handleClose={handleClose}
            handleSave={retrieveInsWithVitaleCard}
            saveTitle={withPatients ? 'RÉCUPÉRER IDENTITÉ INS' : 'ENREGISTRER'}
            title={title}
            loading={loadingIns}
            disableCondition={!isAllWsIns1RequiredDataPresent()}
        >
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    {!withPatients && (
                        <Alert elevation={0} className={classes.alertContainer} severity="info">
                            Afin de récupérer l'INS du patient avec les téléservices INSi, veuillez configurer votre ou
                            vos lecteurs de cartes comme indiqué ci-dessous :
                            <br />
                            <br />
                            1) Insérez votre carte CPS et une carte vitale dans votre ou vos lecteurs de carte mono ou
                            bi-fente et cliquez sur "DÉTECTER LES LECTEURS DE CARTE".
                            <br />
                            2) Choisissez parmis la liste de choix le lecteur pour le type de carte désiré.
                            <br />
                            3) Configurez les lecteurs et renseignez le code PIN associé à la carte CPS.
                            <br />
                            4) Cliquez sur le bouton "ENREGISTRER".
                        </Alert>
                    )}
                    {withPatients && (
                        <Alert className={classes.alertContainer} severity="info">
                            {
                                "Afin de récupérer l'identité INS du patient, veuillez suivre les instructions affichées ci-dessous :"
                            }{' '}
                            <br />
                            <br />
                            1) Insérez votre carte CPS et la carte vitale du patient dans vos lecteurs de carte mono ou
                            bi-fente et cliquez sur "DÉTECTER LES LECTEURS DE CARTE". <br />
                            2) Sélectionnez votre carte CPS et entrez votre code PIN. <br />
                            3) Sélectionnez la carte vitale du patient puis cliquer sur "LIRE CARTE VITALE". <br />
                            4) Sélectionnez le patient à l'aide de la liste déroulante. <br />
                            5) Cliquez sur le bouton "RÉCUPÉRER IDENTITÉ INS". <br />
                        </Alert>
                    )}
                </Grid>
                <Grid item xs={12}>
                    <Grid container direction="row" justifyContent="center" alignItems="center">
                        <Grid item xs={12} md={5}>
                            <LoadingButton
                                variant="outlined"
                                color="primary"
                                elevation={0}
                                action={detectCardReaders}
                                label="DÉTECTER LES LECTEURS DE CARTE"
                                mustDispatchSuccess={false}
                                className={classes.dialogButton}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Grid container direction="row" justifyContent="flex-start" alignItems="center" spacing={1}>
                        <Grid item xs={12} md={1}>
                            <Typography variant="h6">Lecteur carte CPS</Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            {wsIns1Data.cards.length === 0 && wsIns1Data.cpsCard ? (
                                <TokTokDocSelect
                                    id=""
                                    name="document"
                                    variant="outlined"
                                    label="Sélectionner une carte CPS"
                                    value={wsIns1Data.cpsCard}
                                    options={[
                                        {
                                            // @ts-expect-error TS(2339) FIXME: Property 'name' does not exist on type 'string'.
                                            label: wsIns1Data.cpsCard.name,
                                            value: wsIns1Data.cpsCard,
                                        },
                                    ]}
                                    disabled
                                />
                            ) : (
                                <TokTokDocSelect
                                    id=""
                                    name="document"
                                    variant="outlined"
                                    label="Sélectionner une carte CPS"
                                    onChange={(e: any) => {
                                        handleCpsCardChange(e.target.value)
                                    }}
                                    value={wsIns1Data.cpsCard}
                                    options={wsIns1Data.cards}
                                    disabled={wsIns1Data.cards.length === 0}
                                />
                            )}
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                className={classes.dialogButton}
                                id="outlined-basic"
                                placeholder="CODE PIN"
                                variant="outlined"
                                type="password"
                                onChange={(e) => {
                                    handlePinChange(e.target.value)
                                }}
                                value={wsIns1Data.pin}
                                disabled={wsIns1Data.cpsCard === ''}
                                inputProps={{
                                    style: {
                                        textAlign: 'center',
                                    },
                                    maxLength: 4,
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4} />
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Grid container direction="row" justifyContent="flex-start" alignItems="center" spacing={1}>
                        <Grid item xs={12} md={1}>
                            <Typography variant="h6">Lecteur carte vitale</Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            {wsIns1Data.cards.length === 0 && wsIns1Data.vitaleCard ? (
                                <TokTokDocSelect
                                    id=""
                                    name="document"
                                    variant="outlined"
                                    label="Sélectionner une carte vitale"
                                    value={wsIns1Data.vitaleCard}
                                    options={[
                                        {
                                            // @ts-expect-error TS(2339) FIXME: Property 'name' does not exist on type 'string'.
                                            label: wsIns1Data.vitaleCard.name,
                                            value: wsIns1Data.vitaleCard,
                                        },
                                    ]}
                                    disabled
                                />
                            ) : (
                                <TokTokDocSelect
                                    id=""
                                    name="document"
                                    variant="outlined"
                                    label="Sélectionner une carte vitale"
                                    onChange={(e: any) => {
                                        handleVitaleCardChange(e.target.value)
                                    }}
                                    value={wsIns1Data.vitaleCard}
                                    options={wsIns1Data.cards}
                                    disabled={wsIns1Data.cards.length === 0}
                                />
                            )}
                        </Grid>
                        {withPatients && (
                            <Grid item xs={12} md={3}>
                                <LoadingButton
                                    variant="outlined"
                                    color="primary"
                                    elevation={0}
                                    action={readVitaleCard}
                                    label="LIRE CARTE VITALE"
                                    mustDispatchSuccess={false}
                                    className={classes.dialogButton}
                                    disableCondition={wsIns1Data.vitaleCard === ''}
                                />
                            </Grid>
                        )}
                        {withPatients && (
                            <Grid item xs={12} md={4}>
                                <TokTokDocSelect
                                    id=""
                                    name="document"
                                    variant="outlined"
                                    label="Sélectionner un patient"
                                    onChange={handlePatientChange}
                                    value={wsIns1Data.patient}
                                    options={wsIns1Data.patients}
                                    disabled={wsIns1Data.patients.length === 0}
                                />
                            </Grid>
                        )}
                    </Grid>
                </Grid>
                {insiError && (
                    <Grid item xs={12}>
                        <Alert className={classes.alertContainer} severity="error">
                            Impossible de récupérer l'identité INS du patient pour les raisons suivantes : <br />
                            <br />
                            {`- ${insiError}`}
                        </Alert>
                    </Grid>
                )}
            </Grid>
        </TokTokDocDialog>
    )
}

export default INSiConfigModal
